/**
 * The longest `query` the search endpoint accepts. A longer one is rejected with
 * `400 invalid_parameters` — "Key: 'SearchSeriesRequest.Query' Error:Field validation for
 * 'Query' failed on the 'max' tag" — so nothing renders and the failure is logged per request.
 * Reached in production by `/search?q=…` deep links carrying scraped page text rather than by
 * anyone typing: a translation proxy or extension dumping a feed's visible text into the box.
 *
 * Counted in code points, not UTF-16 units or bytes: 128 `á` and 128 emoji both pass, 129 `á`
 * does not (measured against the endpoint). `clampSearchQuery` is what enforces it.
 */
export const SEARCH_QUERY_MAX_LENGTH = 128;

/**
 * Truncate a search query to what the endpoint accepts, by code point.
 *
 * `String.prototype.slice` would count UTF-16 units and can split a surrogate pair, leaving a
 * lone surrogate that makes `encodeURIComponent` throw `URIError: malformed URI sequence` —
 * trading a 400 for a crash. Spreading into an array iterates code points, so a pair is kept
 * whole and the count matches the one the backend validates.
 */
export const clampSearchQuery = (query: string): string => {
    const codePoints = [...query];
    return codePoints.length <= SEARCH_QUERY_MAX_LENGTH ? query : codePoints.slice(0, SEARCH_QUERY_MAX_LENGTH).join('');
};

/**
 * The same clamp applied to a params object on its way to `/search/series`, so the wire is
 * never the first place an over-long query is noticed. The URL parser and the input `maxLength`
 * are what keep a query short in the first place; this is the backstop for every other caller —
 * a programmatic query built from data, a future call site that skips both.
 */
export const clampSearchParamsQuery = <T extends { query?: string }>(params: T): T =>
    typeof params.query === 'string' ? { ...params, query: clampSearchQuery(params.query) } : params;

// Navbar search
export const SERIES_SEARCH_NAV_BAR_PAGE_SIZE = 25;
export const SERIES_SEARCH_NAV_BAR_CLOSED_LOOPBACK_DAYS = 7;
export const SERIES_SEARCH_NAV_BAR_QUERY_SEARCH_FUZZY_THRESHOLD = 4;
export const SOCIAL_SEARCH_NAV_BAR_PAGE_SIZE = 5;

// Sports search. Sized for the first page, which is server-rendered into the all-sports and
// archived-competition grids; infinite scroll fetches the rest in pages of the same size.
export const SERIES_SEARCH_SPORTS_PAGE_SIZE = 12;
export const SERIES_SEARCH_SPORTS_PROPS_PAGE_SIZE = 80;

// Related series search
export const SERIES_SEARCH_RELATED_SERIES_PAGE_SIZE = 10;
